import { useEffect } from "react";

import useDecodedParams from "Hooks/useDecodedParams";
import {
  domainSelector,
  domainsLoadingSelector,
  domainsErrorSelector,
  domainsStatusSelector,
  domainPermissionSelector
} from "Reducers/project/settings/domains/domains.selectors";
import { deleteDomainThunk } from "Reducers/project/settings/domains/thunks/deleteDomain.thunk";
import { getDomainsThunk } from "Reducers/project/settings/domains/thunks/getDomains.thunk";
import { updateDefaultDomain } from "src/reducers/project/settings/domains/thunks/updateDefaultDomain.thunk";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import type { Domain, Project } from "@packages/client";

export const useLoadDomain = (project?: Project) => {
  const dispatch = useAppDispatch();
  const { organizationId, domainId, projectId } = useDecodedParams<{
    organizationId: string;
    domainId: string;
    projectId: string;
  }>();

  const domain = useAppSelector(state =>
    domainSelector(state, { domainId, projectId })
  );
  const loading = useAppSelector(state =>
    domainsLoadingSelector(state, { projectId })
  );
  const errors = useAppSelector(state =>
    domainsErrorSelector(state, { projectId })
  );
  const status = useAppSelector(state =>
    domainsStatusSelector(state, { projectId })
  );

  const hasDomainPermission = useAppSelector(state =>
    domainPermissionSelector(state, { project })
  );

  useEffect(() => {
    if (project?.id) {
      dispatch(getDomainsThunk({ project }));
    }
  }, [project?.id, project, dispatch]);

  const handleDelete = (domain: Domain, newDefaultDomain?: Domain) => {
    if (domain && projectId) {
      dispatch(
        deleteDomainThunk({
          domain,
          defaultDomain: newDefaultDomain,
          projectId,
          organizationId
        })
      );
    }
  };

  const handleSetDefault = (domainId?: string) => {
    if (!domainId) return;
    dispatch(updateDefaultDomain({ domainId, organizationId, projectId }));
  };

  return {
    domain,
    loading,
    errors,
    status,
    handleDelete,
    handleSetDefault,
    hasDomainPermission
  };
};
